/**
 * A kind's periods as a timeline of 24h rows, one `1d`, `2d`, ... label to the left of each. A
 * period is a button whose width is its share of the row's 24h; one crossing midnight is split
 * into a piece per row, every piece opening the same period. Clicking a piece opens that
 * period's fields in a floating card anchored to it, one at a time. A period's start is the sum
 * of the durations before it, so adding or removing a period shifts every later one rather than
 * overlapping them.
 *
 * A piece too narrow for its frequency and duration shows the frequency alone, then no text at
 * all, keeping its tooltip and accessible name -- the bar stays readable at any width instead of
 * clipping mid-glyph. The "+" sits in the empty end of the last row when it fits there, and on
 * a row of its own otherwise: it never shrinks a piece.
 */
import { Clock, Plus, RefreshCw, TriangleAlert, Trash2 } from 'lucide-react';
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefCallback,
} from 'react';

import { DurationInput, formatDuration, type DurationUnit } from '../../../../chrome/form';
import { Button } from '../../../../ui/button';
import { Field, FieldLabel } from '../../../../ui/field';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../ui/tooltip';
import { profileCatalogue } from './messages';
import { PeriodColorPicker } from './PeriodColorPicker';
import {
  compactDuration,
  DAY_SECONDS,
  dayRows,
  MIN_FREQUENCY_SECONDS,
  nextPeriod,
  periodSwatch,
  swatchVars,
  totalSeconds,
  transitionShortened,
  type DayPiece,
  type PeriodDraft,
} from './periods';

const t = profileCatalogue.t;
const SPAN_UNITS: DurationUnit[] = ['minutes', 'hours', 'days'];
//: The longest a single period may run; the whole profile's ceiling is the api's.
const MAX_PERIOD_SECONDS = 90 * 86400;
//: The "+" control is h-7 w-7.
const ADD_WIDTH = 28;

function short(seconds: number | null): string {
  return seconds === null ? '—' : compactDuration(seconds);
}

function useElementWidth<T extends HTMLElement>(): [RefCallback<T>, number] {
  const [width, setWidth] = useState(0);
  const observer = useRef<ResizeObserver | null>(null);
  const setRef = useCallback<RefCallback<T>>((node) => {
    observer.current?.disconnect();
    observer.current = null;
    if (node === null) return;
    setWidth(node.clientWidth);
    observer.current = new ResizeObserver(() => setWidth(node.clientWidth));
    observer.current.observe(node);
  }, []);
  return [setRef, width];
}

function segmentLabel(period: PeriodDraft, variant: 'full' | 'freq'): ReactNode {
  return (
    <>
      <RefreshCw aria-hidden className="size-[10.8px] shrink-0" />
      {short(period.frequency_seconds)}
      {variant === 'full' && (
        <>
          <Clock aria-hidden className="ml-1 size-[10.8px] shrink-0" />
          {short(period.duration_seconds)}
        </>
      )}
    </>
  );
}

type Fit = 'full' | 'freq' | 'none';

function Piece(props: {
  index: number;
  piece: DayPiece;
  period: PeriodDraft;
  /** The row's first and last piece round the bar's ends. */
  edges: { start: boolean; end: boolean };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
}): ReactNode {
  const { period, piece } = props;
  const [buttonRef, containerWidth] = useElementWidth<HTMLButtonElement>();
  const [fit, setFit] = useState<Fit>('full');
  const contentRef = useRef<HTMLSpanElement>(null);

  // A resize or an edited period can free up room the piece did not have before, so every
  // change retries the widest variant; the effect below then measures the real painted content
  // (never a separate invisible clone) and downgrades it step by step until it actually fits.
  // The reset commit still paints the previous variant, so measuring in it would downgrade from a
  // stale width: the measuring effect skips that commit and runs on the one that paints `full`.
  const resetting = useRef(false);
  const [pass, setPass] = useState(0);
  useLayoutEffect(() => {
    resetting.current = true;
    setFit('full');
    setPass((current) => current + 1);
  }, [containerWidth, period.frequency_seconds, period.duration_seconds]);

  useLayoutEffect(() => {
    if (resetting.current) {
      resetting.current = false;
      return;
    }
    const content = contentRef.current;
    if (content === null || fit === 'none') return;
    if (content.scrollWidth > content.clientWidth) {
      setFit((current) => (current === 'full' ? 'freq' : 'none'));
    }
  }, [fit, containerWidth, pass]);

  const label = t('period.segment', {
    n: props.index + 1,
    frequency: formatDuration(period.frequency_seconds ?? 0, SPAN_UNITS),
    duration: formatDuration(period.duration_seconds ?? 0, SPAN_UNITS),
  });
  const vars = swatchVars(periodSwatch(period));
  const rounding = `${props.edges.start ? 'rounded-l-md ' : ''}${props.edges.end ? 'rounded-r-md ' : ''}`;

  return (
    <Popover open={props.open} onOpenChange={props.onOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              ref={buttonRef}
              type="button"
              aria-label={label}
              disabled={props.disabled}
              tabIndex={piece.first ? undefined : -1}
              data-swatch={periodSwatch(period)}
              data-testid={
                piece.first ? `period-segment-${props.index}` : `period-segment-${props.index}-more`
              }
              style={{ ...vars, width: `${(piece.seconds / DAY_SECONDS) * 100}%` }}
              className={`relative flex h-7 min-w-2 shrink-0 items-center overflow-hidden text-[10.8px] font-medium outline-none focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset aria-expanded:ring-2 aria-expanded:ring-ring aria-expanded:ring-inset ${rounding}`}
            >
              <span
                ref={contentRef}
                className="flex w-full items-center justify-center gap-1 overflow-hidden whitespace-nowrap tabular-nums"
              >
                {fit !== 'none' && segmentLabel(period, fit)}
              </span>
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        collisionPadding={16}
        className="w-80 max-w-[calc(100vw-2rem)]"
        aria-label={t('period.title', { n: props.index + 1 })}
      >
        {props.children}
      </PopoverContent>
    </Popover>
  );
}

function DayLabel(props: { day: number }): ReactNode {
  return (
    <span className="w-8 shrink-0 text-caption text-muted-foreground tabular-nums">
      {t('period.day', { n: props.day })}
    </span>
  );
}

function OpenPeriod(props: {
  index: number;
  period: PeriodDraft;
  start: number;
  minFrequencySeconds: number;
  units: DurationUnit[];
  onChange: (next: PeriodDraft) => void;
  onRemove: (() => void) | null;
  disabled?: boolean;
}): ReactNode {
  const { period, index } = props;
  const n = index + 1;
  const end = props.start + (period.duration_seconds ?? 0);
  const warn = transitionShortened(period);
  return (
    <div className="flex flex-col gap-3" data-testid={`period-row-${index}`}>
      {/* The remove button stays pinned to this same row, right-aligned, at every width --
       * `items-start` so it does not re-center if the title/range wraps to two lines, never
       * `flex-wrap` on the row itself, which would let the button wrap onto a row of its own. */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-medium">{t('period.title', { n })}</span>
          <span className="text-caption text-muted-foreground">
            {t('period.range', {
              start: props.start === 0 ? t('period.start') : compactDuration(props.start),
              end: compactDuration(end),
            })}
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0"
          disabled={props.disabled || props.onRemove === null}
          title={props.onRemove === null ? t('period.remove.only') : undefined}
          aria-label={t('period.remove', { n })}
          onClick={props.onRemove ?? undefined}
        >
          <Trash2 aria-hidden className="size-4" />
        </Button>
      </div>
      <div className="flex flex-col gap-3">
        <Field>
          <FieldLabel htmlFor={`period-${index}-frequency`}>{t('period.frequency')}</FieldLabel>
          <DurationInput
            id={`period-${index}-frequency`}
            seconds={period.frequency_seconds}
            minSeconds={props.minFrequencySeconds}
            units={props.units}
            disabled={props.disabled}
            onChange={(seconds) => props.onChange({ ...period, frequency_seconds: seconds })}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={`period-${index}-duration`}>{t('period.duration')}</FieldLabel>
          <DurationInput
            id={`period-${index}-duration`}
            seconds={period.duration_seconds}
            minSeconds={Math.max(period.frequency_seconds ?? 0, props.minFrequencySeconds)}
            maxSeconds={MAX_PERIOD_SECONDS}
            units={props.units}
            disabled={props.disabled}
            onChange={(seconds) => props.onChange({ ...period, duration_seconds: seconds })}
          />
        </Field>
      </div>
      <PeriodColorPicker
        n={n}
        value={period.color}
        resolved={periodSwatch({ ...period, color: null })}
        disabled={props.disabled}
        onChange={(color) => props.onChange({ ...period, color })}
      />
      {warn && (
        <p
          role="status"
          className="flex items-start gap-2 rounded-md bg-warning-quiet px-2 py-1.5 text-caption text-warning-quiet-foreground"
        >
          <TriangleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
          {t('period.transition')}
        </p>
      )}
    </div>
  );
}

export function PeriodEditor(props: {
  periods: PeriodDraft[];
  onChange: (periods: PeriodDraft[]) => void;
  /** The api's floor on a frequency for this kind. */
  minFrequencySeconds?: number;
  disabled?: boolean;
}): ReactNode {
  const { periods, onChange } = props;
  const minFrequency = props.minFrequencySeconds ?? MIN_FREQUENCY_SECONDS;
  const units: DurationUnit[] = minFrequency >= 3600 ? ['hours', 'days'] : SPAN_UNITS;
  // The piece the card is anchored to, by period and absolute start so a period split across
  // rows opens beside the piece that was clicked.
  const [open, setOpen] = useState<{ index: number; at: number } | null>(null);
  const [trackRef, trackWidth] = useElementWidth<HTMLDivElement>();

  const set = (index: number, next: PeriodDraft) =>
    onChange(periods.map((period, at) => (at === index ? next : period)));

  let start = 0;
  const starts = periods.map((period) => {
    const at = start;
    start += period.duration_seconds ?? 0;
    return at;
  });

  const rows = dayRows(periods);
  const pieces = rows.flatMap((row) => row.pieces);
  // A period's pieces can vanish under an edit (a shorter duration drops its second row), so an
  // anchor that is gone falls back to the period's first piece instead of closing the card.
  const anchor =
    open === null
      ? null
      : (pieces.find((piece) => piece.index === open.index && piece.startSeconds === open.at) ??
        pieces.find((piece) => piece.index === open.index && piece.first) ??
        null);

  const lastRow = rows.at(-1);
  const room =
    lastRow === undefined ? 0 : ((DAY_SECONDS - lastRow.usedSeconds) / DAY_SECONDS) * trackWidth;
  const addInline = lastRow !== undefined && room >= ADD_WIDTH;

  const addButton = (inline: boolean) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={`h-7 w-7 shrink-0 ${inline ? 'ml-1' : ''}`}
          disabled={props.disabled}
          aria-label={t('period.add')}
          onClick={() => {
            const next = [...periods, nextPeriod(periods, minFrequency)];
            onChange(next);
            setOpen({ index: next.length - 1, at: totalSeconds(periods) });
          }}
        >
          <Plus aria-hidden className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{t('period.add')}</TooltipContent>
    </Tooltip>
  );

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div
        role="group"
        aria-label={t('period.timeline')}
        className="flex flex-col gap-1"
        data-testid="period-timeline"
      >
        {rows.map((row, rowIndex) => {
          const isLast = rowIndex === rows.length - 1;
          return (
            <div
              key={row.day}
              data-testid={`day-row-${row.day}`}
              className="flex items-center gap-2"
            >
              <DayLabel day={row.day} />
              <div ref={isLast ? trackRef : undefined} className="flex h-7 min-w-0 flex-1">
                {row.pieces.map((piece, at) => (
                  <Piece
                    key={`${piece.index}@${piece.startSeconds}`}
                    index={piece.index}
                    piece={piece}
                    period={periods[piece.index]!}
                    edges={{ start: at === 0, end: at === row.pieces.length - 1 }}
                    open={piece === anchor}
                    onOpenChange={(next) =>
                      // A dismissal can land after the click that opened another period (the
                      // outside press is reported late), so a close only applies to the period
                      // that is still the open one.
                      setOpen((current) =>
                        next
                          ? { index: piece.index, at: piece.startSeconds }
                          : current?.index === piece.index
                            ? null
                            : current,
                      )
                    }
                    disabled={props.disabled}
                  >
                    {piece === anchor && (
                      <OpenPeriod
                        index={piece.index}
                        period={periods[piece.index]!}
                        start={starts[piece.index]!}
                        minFrequencySeconds={minFrequency}
                        units={units}
                        disabled={props.disabled}
                        onChange={(next) => set(piece.index, next)}
                        onRemove={
                          periods.length > 1
                            ? () => {
                                onChange(periods.filter((_period, i) => i !== piece.index));
                                setOpen(null);
                              }
                            : null
                        }
                      />
                    )}
                  </Piece>
                ))}
                {isLast && addInline && addButton(true)}
              </div>
            </div>
          );
        })}
        {!addInline && (
          <div data-testid={`day-row-${rows.length + 1}`} className="flex items-center gap-2">
            <DayLabel day={rows.length + 1} />
            <div className="flex h-7 min-w-0 flex-1">{addButton(false)}</div>
          </div>
        )}
      </div>
      <p className="text-caption text-muted-foreground">
        {t('period.total', { total: formatDuration(totalSeconds(periods), SPAN_UNITS) })}
      </p>
    </div>
  );
}
